import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { nationalityReducer } from "../../../state/reducers";
import { INationality } from "../../../utils/types/models";

const NationalityEditor: React.FC<IAddEntityScreenProps<INationality> | IEditEntityScreenProps<INationality>> = (props: IAddEntityScreenProps<INationality> | IEditEntityScreenProps<INationality>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(nationalityReducer, getInitialState<INationality>());
	const initialNationality: INationality = {
		id: (props as IEditEntityScreenProps<INationality>).recordId || 0,
		name: "",
		active: true
	};
	const [nationality, setNationality] = useState<INationality>(initialNationality);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setNationality(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setNationality(handleChange(nationality, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(nationality.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: INationality): void {
		if (record && !close) {
			setNationality(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<INationality>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={nationality}
			endpoint={Endpoint.Nationalities}
			entityState={state}
			entityType={t("nationality")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={nationality.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} value={nationality.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={nationality.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default NationalityEditor;
